import {
  TCT_CURL,
  TCT_GZIP_BASE64,
  TCT_GZIP_ETAG,
  TCT_GZIP_LENGTH,
  TCT_MURL,
  TCT_MURL_CONTENT_DIGEST,
  TCT_MURL_ETAG,
  TCT_MURL_HINT,
  TCT_MURL_IDENTITY,
  TCT_MURL_PROFILE,
  TCT_REWRITTEN_IDENTITY,
  TCT_SITEMAP_CONTENT_DIGEST,
  TCT_SITEMAP_ETAG,
  TCT_SITEMAP_IDENTITY,
  TCT_SITEMAP_PROFILE,
} from "./fixtures";
import type {
  TctScenario,
  TctSimulationResult,
  TctStage,
  TctStageState,
  TctTimelineStep,
} from "./types";

const encoder = new TextEncoder();

function byteLength(value: string): number {
  return encoder.encode(value).byteLength;
}

function request(
  method: "GET" | "HEAD",
  path: string,
  headers: readonly string[] = [],
): string {
  return [`${method} ${path} HTTP/1.1`, "Host: example.com", ...headers, ""].join(
    "\n",
  );
}

function response(
  status: string,
  headers: readonly string[],
  body = "",
): string {
  return [
    `HTTP/1.1 ${status}`,
    ...headers,
    `Content-Length: ${byteLength(body)}`,
    "",
    body,
  ].join("\n");
}

function metadataOnlyResponse(
  status: string,
  headers: readonly string[],
): string {
  return [`HTTP/1.1 ${status}`, ...headers, "", ""].join("\n");
}

function binaryResponse(
  status: string,
  headers: readonly string[],
  contentLength: number,
): string {
  return [
    `HTTP/1.1 ${status}`,
    ...headers,
    `Content-Length: ${contentLength}`,
    "",
    `[${contentLength} binary representation octets; inventoried separately]`,
  ].join("\n");
}

function exchange(requestMessage: string, responseMessage: string): string {
  return `${requestMessage}\n${responseMessage}`;
}

function murlResponse(
  etag = TCT_MURL_ETAG,
  links = true,
  body = TCT_MURL_IDENTITY,
): string {
  const headers = [
    "Content-Type: application/json",
    `ETag: ${etag}`,
    `Content-Digest: ${TCT_MURL_CONTENT_DIGEST}`,
    "Vary: Accept-Encoding",
  ];
  if (links) {
    headers.push(
      `Link: <${TCT_CURL}>; rel="canonical"`,
      `Link: <${TCT_MURL_PROFILE}>; rel="profile"`,
    );
  }
  return response("200 OK", headers, body);
}

function sitemapResponse(body = TCT_SITEMAP_IDENTITY): string {
  return response(
    "200 OK",
    [
      "Content-Type: application/json",
      `ETag: ${TCT_SITEMAP_ETAG}`,
      `Content-Digest: ${TCT_SITEMAP_CONTENT_DIGEST}`,
      "Vary: Accept-Encoding",
      `Link: <${TCT_SITEMAP_PROFILE}>; rel="profile"`,
    ],
    body,
  );
}

function stages(
  overrides: Partial<Record<TctStage, TctStageState>>,
): Readonly<Record<TctStage, TctStageState>> {
  return {
    entry: "idle",
    catalog: "idle",
    murl: "idle",
    curl: "idle",
    ...overrides,
  };
}

function discoveryResult(scenario: TctScenario): TctSimulationResult {
  const steps: readonly TctTimelineStep[] = [
    {
      number: "01",
      eyebrow: "Generic discovery",
      title: "Observe a candidate catalog link",
      detail:
        "The successful entry response advertises JSON through the generic index relation. The client records a candidate and does not infer TCT yet.",
      wire: exchange(
        request("GET", "/", ["Accept: text/html"]),
        response("200 OK", [
          "Content-Type: text/html",
          'Link: </catalog-v2.json>; rel="index"; type="application/json"',
        ]),
      ),
      tone: "neutral",
    },
    {
      number: "02",
      eyebrow: "Profile confirmation",
      title: "Retrieve and certify the M-Sitemap",
      detail:
        "Exact application/json, profile, version 2, required item structure, and a strong catalog ETag confirm this candidate as a Draft-03 M-Sitemap.",
      wire: exchange(
        request("GET", "/catalog-v2.json", [
          "Accept: application/json",
          "Accept-Encoding: identity",
        ]),
        sitemapResponse(),
      ),
      tone: "success",
    },
    {
      number: "03",
      eyebrow: "Advertised mapping",
      title: "Learn the C-URL, M-URL, and identity hint",
      detail:
        "The item maps the human-facing resource to its machine-facing alternate. The unquoted hint is advisory and belongs to the identity M-URL.",
      wire: [
        `C-URL: ${TCT_CURL}`,
        `M-URL: ${TCT_MURL}`,
        `Identity hint: ${TCT_MURL_HINT}`,
        "Hint authority: catalog snapshot",
      ].join("\n"),
      tone: "success",
    },
  ];

  return {
    scenarioId: scenario.id,
    layerLabel: "Discovery + catalog confirmation",
    outcome: "confirmed",
    outcomeLabel: "Draft-03 catalog confirmed",
    explanation:
      "TCT was established from the retrieved catalog's exact profile and structure, not from the generic index link or a guessed path.",
    requestCount: 2,
    stages: stages({
      entry: "observed",
      catalog: "confirmed",
      murl: "candidate",
      curl: "observed",
    }),
    steps,
  };
}

function identityResult(scenario: TctScenario): TctSimulationResult {
  return {
    scenarioId: scenario.id,
    layerLabel: "Origin identity representation",
    outcome: "confirmed",
    outcomeLabel: "Identity bytes certified",
    explanation:
      "The selected body is the exact JCS byte sequence bound by the strong response ETag. Content-Digest repeats its SHA-256 integrity metadata without creating authority.",
    requestCount: 1,
    stages: stages({
      entry: "observed",
      catalog: "confirmed",
      murl: "confirmed",
      curl: "confirmed",
    }),
    steps: [
      {
        number: "01",
        eyebrow: "Identity selection",
        title: "Request the deterministic M-URL representation",
        detail:
          "Accept-Encoding explicitly selects identity. The response carries exact JSON, the body-derived validator, and both required links.",
        wire: exchange(
          request("GET", "/article/123/llm.json", [
            "Accept: application/json",
            "Accept-Encoding: identity",
          ]),
          murlResponse(),
        ),
        tone: "success",
      },
      {
        number: "02",
        eyebrow: "Byte identity",
        title: "Bind one body to two integrity fields",
        detail:
          "The hexadecimal ETag and base64 Content-Digest encode SHA-256 over the same 333 UTF-8 identity bytes.",
        wire: [
          "Identity bytes: 333",
          `ETag: ${TCT_MURL_ETAG}`,
          `Content-Digest: ${TCT_MURL_CONTENT_DIGEST}`,
          "Authentication claim: none",
        ].join("\n"),
        tone: "success",
      },
    ],
  };
}

function conditionalResult(scenario: TctScenario): TctSimulationResult {
  return {
    scenarioId: scenario.id,
    layerLabel: "Target-resource revalidation",
    outcome: "not_modified",
    outcomeLabel: "304 · Cached identity reused",
    explanation:
      "The quoted hint matches the current selected identity representation. The empty 304 response carries the current authoritative ETag.",
    requestCount: 1,
    stages: stages({
      entry: "observed",
      catalog: "confirmed",
      murl: "reused",
      curl: "confirmed",
    }),
    steps: [
      {
        number: "01",
        eyebrow: "Condition construction",
        title: "Quote the validated identity hint",
        detail:
          "The client accepts the exact lowercase sha256 form, adds HTTP quotes, stays on the same origin, and requests identity.",
        wire: [
          `Catalog value: ${TCT_MURL_HINT}`,
          `HTTP field: If-None-Match: ${TCT_MURL_ETAG}`,
          "Selected coding: identity",
          "Origin match: yes",
        ].join("\n"),
        tone: "neutral",
      },
      {
        number: "02",
        eyebrow: "Conditional GET",
        title: "Revalidate the selected M-URL",
        detail:
          "A matching If-None-Match condition returns 304 with no content and the current response ETag.",
        wire: exchange(
          request("GET", "/article/123/llm.json", [
            "Accept: application/json",
            "Accept-Encoding: identity",
            `If-None-Match: ${TCT_MURL_ETAG}`,
          ]),
          metadataOnlyResponse("304 Not Modified", [
            `ETag: ${TCT_MURL_ETAG}`,
            "Vary: Accept-Encoding",
          ]),
        ),
        tone: "success",
      },
    ],
  };
}

function catalogReuseResult(scenario: TctScenario): TctSimulationResult {
  return {
    scenarioId: scenario.id,
    layerLabel: "Client cache optimization",
    outcome: "reused",
    outcomeLabel: "M-URL request skipped",
    explanation:
      "The client elects to reuse cached identity bytes after catalog revalidation. Their freshness is no stronger than the catalog and the publisher's update behavior.",
    requestCount: 1,
    stages: stages({
      entry: "observed",
      catalog: "confirmed",
      murl: "reused",
      curl: "confirmed",
    }),
    steps: [
      {
        number: "01",
        eyebrow: "Catalog revalidation",
        title: "Refresh the snapshot that carries the hint",
        detail:
          "The M-Sitemap's own ETag validates the catalog representation, independently from the M-URL hint inside it.",
        wire: exchange(
          request("GET", "/catalog-v2.json", [
            "Accept: application/json",
            "Accept-Encoding: identity",
            `If-None-Match: ${TCT_SITEMAP_ETAG}`,
          ]),
          metadataOnlyResponse("304 Not Modified", [
            `ETag: ${TCT_SITEMAP_ETAG}`,
            "Vary: Accept-Encoding",
          ]),
        ),
        tone: "success",
      },
      {
        number: "02",
        eyebrow: "Bounded skip",
        title: "Compare the cached identity validator to the hint",
        detail:
          "Equal opaque values permit a local reuse decision. A client needing later target validation still sends a conditional M-URL request.",
        wire: [
          `Cached M-URL ETag: ${TCT_MURL_ETAG}`,
          `Catalog identity hint: ${TCT_MURL_HINT}`,
          "Comparison: equal",
          "M-URL network request: skipped by client policy",
        ].join("\n"),
        tone: "warning",
      },
    ],
  };
}

function bidirectionalResult(scenario: TctScenario): TctSimulationResult {
  const html = "<h1>Understanding HTTP Validators</h1>";
  return {
    scenarioId: scenario.id,
    layerLabel: "Bidirectional discovery",
    outcome: "confirmed",
    outcomeLabel: "Mapping confirmed both ways",
    explanation:
      "The C-URL advertises an alternate, while the profiled M-URL names the C-URL as canonical. Profile validation remains the proof step.",
    requestCount: 2,
    stages: stages({
      entry: "observed",
      catalog: "confirmed",
      murl: "confirmed",
      curl: "confirmed",
    }),
    steps: [
      {
        number: "01",
        eyebrow: "Forward discovery",
        title: "Find the machine-facing alternate",
        detail:
          "The C-URL advertises an application/json alternate. That link remains a candidate until the target is profiled.",
        wire: exchange(
          request("GET", "/article/123", ["Accept: text/html"]),
          response(
            "200 OK",
            [
              "Content-Type: text/html",
              'Link: </article/123/llm.json>; rel="alternate"; type="application/json"',
            ],
            html,
          ),
        ),
        tone: "neutral",
      },
      {
        number: "02",
        eyebrow: "Reverse discovery",
        title: "Confirm the profile and canonical C-URL",
        detail:
          "The M-URL response supplies both the exact Draft-03 profile link and the resolved canonical link back to the human-facing resource.",
        wire: exchange(
          request("GET", "/article/123/llm.json", [
            "Accept: application/json",
            "Accept-Encoding: identity",
          ]),
          murlResponse(),
        ),
        tone: "success",
      },
    ],
  };
}

function codedVariantResult(scenario: TctScenario): TctSimulationResult {
  return {
    scenarioId: scenario.id,
    layerLabel: "Content negotiation",
    outcome: "observed",
    outcomeLabel: "Valid gzip representation",
    explanation:
      "The gzip bytes form another selected representation with a different strong ETag. Vary keeps caches from combining it with identity.",
    requestCount: 1,
    stages: stages({
      entry: "observed",
      catalog: "confirmed",
      murl: "confirmed",
      curl: "confirmed",
    }),
    steps: [
      {
        number: "01",
        eyebrow: "Coded selection",
        title: "Permit gzip and inspect its metadata",
        detail:
          "The response identifies 254 fixed gzip octets. Binary bytes are inventoried below rather than rendered as text inside the HTTP display.",
        wire: exchange(
          request("GET", "/article/123/llm.json", [
            "Accept: application/json",
            "Accept-Encoding: gzip",
          ]),
          binaryResponse(
            "200 OK",
            [
              "Content-Type: application/json",
              "Content-Encoding: gzip",
              `ETag: ${TCT_GZIP_ETAG}`,
              "Vary: Accept-Encoding",
            ],
            TCT_GZIP_LENGTH,
          ),
        ),
        tone: "success",
      },
      {
        number: "02",
        eyebrow: "Fixture identity",
        title: "Keep identity and coded validators separate",
        detail:
          "The fixed gzip fixture decompresses to the identity JCS bytes, but its coded representation data and strong ETag are different.",
        wire: [
          `Identity ETag: ${TCT_MURL_ETAG}`,
          `Gzip ETag:     ${TCT_GZIP_ETAG}`,
          `Gzip bytes: ${TCT_GZIP_LENGTH}`,
          `Gzip fixture (base64): ${TCT_GZIP_BASE64}`,
          "Catalog hint target: identity only",
        ].join("\n"),
        tone: "success",
      },
    ],
  };
}

function weakEtagResult(scenario: TctScenario): TctSimulationResult {
  return {
    scenarioId: scenario.id,
    layerLabel: "Observed public response",
    outcome: "rejected",
    outcomeLabel: "Rejected · Weak validator",
    explanation:
      "The identity response does not satisfy Draft-03 because its ETag is weak, even though the displayed body bytes remain canonical.",
    requestCount: 1,
    stages: stages({
      entry: "observed",
      catalog: "confirmed",
      murl: "rejected",
    }),
    steps: [
      {
        number: "01",
        eyebrow: "Public delivery",
        title: "Observe a weakened identity validator",
        detail:
          "The W/ prefix changes the validator class. Draft-03 requires a strong body-derived ETag for identity TCT JSON.",
        wire: exchange(
          request("GET", "/article/123/llm.json", [
            "Accept: application/json",
            "Accept-Encoding: identity",
          ]),
          murlResponse(`W/${TCT_MURL_ETAG}`),
        ),
        tone: "rejected",
      },
    ],
  };
}

function codedReuseResult(scenario: TctScenario): TctSimulationResult {
  return {
    scenarioId: scenario.id,
    layerLabel: "Intermediary representation handling",
    outcome: "rejected",
    outcomeLabel: "Rejected · ETag reused",
    explanation:
      "The public path selected gzip bytes but attached the identity ETag. Those non-identical representation data cannot share one strong validator.",
    requestCount: 1,
    stages: stages({
      entry: "observed",
      catalog: "confirmed",
      murl: "rejected",
    }),
    steps: [
      {
        number: "01",
        eyebrow: "Public delivery",
        title: "Detect validator reuse across codings",
        detail:
          "Vary is present, but the ETag still identifies the identity bytes rather than the selected gzip representation.",
        wire: exchange(
          request("GET", "/article/123/llm.json", [
            "Accept: application/json",
            "Accept-Encoding: gzip",
          ]),
          binaryResponse(
            "200 OK",
            [
              "Content-Type: application/json",
              "Content-Encoding: gzip",
              `ETag: ${TCT_MURL_ETAG}`,
              "Vary: Accept-Encoding",
            ],
            TCT_GZIP_LENGTH,
          ),
        ),
        tone: "rejected",
      },
      {
        number: "02",
        eyebrow: "Required separation",
        title: "Compare the actual fixture identities",
        detail:
          "The governed gzip fixture hashes to another strong ETag, proving that the public response reused the wrong validator.",
        wire: [
          `Observed: ${TCT_MURL_ETAG}`,
          `Required for gzip fixture: ${TCT_GZIP_ETAG}`,
          "Representation bytes identical: no",
        ].join("\n"),
        tone: "rejected",
      },
    ],
  };
}

function missingLinksResult(scenario: TctScenario): TctSimulationResult {
  return {
    scenarioId: scenario.id,
    layerLabel: "M-URL profile confirmation",
    outcome: "rejected",
    outcomeLabel: "Rejected · Required links absent",
    explanation:
      "A familiar JSON shape and body-derived ETag do not establish the M-URL profile without the exact profile and canonical links.",
    requestCount: 1,
    stages: stages({
      entry: "observed",
      catalog: "confirmed",
      murl: "rejected",
    }),
    steps: [
      {
        number: "01",
        eyebrow: "Candidate retrieval",
        title: "Inspect a shape-compatible JSON response",
        detail:
          "The target provides canonical bytes and a strong ETag but omits both required response links.",
        wire: exchange(
          request("GET", "/article/123/llm.json", [
            "Accept: application/json",
            "Accept-Encoding: identity",
          ]),
          murlResponse(TCT_MURL_ETAG, false),
        ),
        tone: "warning",
      },
      {
        number: "02",
        eyebrow: "Confirmation gate",
        title: "Do not infer TCT from body shape",
        detail:
          "The response lacks rel=profile and rel=canonical. The client rejects it as a confirmed Draft-03 M-URL.",
        wire: [
          "Exact M-URL profile link: missing",
          "Canonical C-URL link: missing",
          "Body shape: familiar but insufficient",
          "TCT processing: stopped",
        ].join("\n"),
        tone: "rejected",
      },
    ],
  };
}

function crossOriginResult(scenario: TctScenario): TctSimulationResult {
  const catalog = JSON.stringify({
    cUrl: TCT_CURL,
    etag: TCT_MURL_HINT,
    mUrl: "https://other.example/article/123/llm.json",
  });
  return {
    scenarioId: scenario.id,
    layerLabel: "Client origin policy",
    outcome: "blocked",
    outcomeLabel: "Blocked · Hint origin mismatch",
    explanation:
      "The client does not send the catalog-derived validator to another origin. The demo performs no cross-origin request.",
    requestCount: 0,
    stages: stages({
      entry: "observed",
      catalog: "confirmed",
      murl: "blocked",
    }),
    steps: [
      {
        number: "01",
        eyebrow: "Catalog observation",
        title: "Find a cross-origin target with an identity hint",
        detail:
          "The item is visible in the same-origin catalog, but its M-URL belongs to other.example.",
        wire: catalog,
        tone: "warning",
      },
      {
        number: "02",
        eyebrow: "Safety boundary",
        title: "Refuse to construct the conditional request",
        detail:
          "Draft-03 prohibits using an ETag hint from one origin as a validator at another. Cross-origin fetching also needs explicit SSRF and credential policy.",
        wire: [
          "Hint origin: example.com",
          "Target origin: other.example",
          "If-None-Match construction: prohibited",
          "Network request: not sent",
        ].join("\n"),
        tone: "rejected",
      },
    ],
  };
}

function intermediaryRewriteResult(scenario: TctScenario): TctSimulationResult {
  return {
    scenarioId: scenario.id,
    layerLabel: "Origin vs public delivery",
    outcome: "rejected",
    outcomeLabel: "Rejected · Bytes and ETag diverged",
    explanation:
      "The observed public body contains one added byte while retaining the origin's identity ETag and digest. The selected representation no longer matches its metadata.",
    requestCount: 1,
    stages: stages({
      entry: "observed",
      catalog: "confirmed",
      murl: "rejected",
    }),
    steps: [
      {
        number: "01",
        eyebrow: "Origin publication",
        title: "Record the certified origin identity",
        detail:
          "The producer binds 333 exact JCS bytes to the strong SHA-256 validator before intermediary handling.",
        wire: [
          "Origin bytes: 333",
          `Origin ETag: ${TCT_MURL_ETAG}`,
          `Origin Content-Digest: ${TCT_MURL_CONTENT_DIGEST}`,
        ].join("\n"),
        tone: "success",
      },
      {
        number: "02",
        eyebrow: "Observed public response",
        title: "Detect an unbound intermediary rewrite",
        detail:
          "One trailing space changes the public bytes, but the response preserves the old validator and digest instead of generating corresponding metadata.",
        wire: exchange(
          request("GET", "/article/123/llm.json", [
            "Accept: application/json",
            "Accept-Encoding: identity",
          ]),
          murlResponse(TCT_MURL_ETAG, true, TCT_REWRITTEN_IDENTITY),
        ),
        tone: "rejected",
      },
      {
        number: "03",
        eyebrow: "Layer attribution",
        title: "Keep implementation and delivery evidence separate",
        detail:
          "The observation proves a public-path mismatch. Without an origin-only bypass, it does not independently identify which intermediary changed the response.",
        wire: [
          "Origin implementation: not independently observed",
          "Intermediary/cache integration: not independently isolated",
          "Public delivery path: identity mismatch observed",
        ].join("\n"),
        tone: "warning",
      },
    ],
  };
}

export function simulateTctScenario(
  scenario: TctScenario,
): TctSimulationResult {
  switch (scenario.kind) {
    case "catalog_discovery":
      return discoveryResult(scenario);
    case "identity_murl":
      return identityResult(scenario);
    case "conditional_304":
      return conditionalResult(scenario);
    case "catalog_reuse":
      return catalogReuseResult(scenario);
    case "bidirectional_links":
      return bidirectionalResult(scenario);
    case "coded_variant":
      return codedVariantResult(scenario);
    case "weak_identity_etag":
      return weakEtagResult(scenario);
    case "coded_etag_reuse":
      return codedReuseResult(scenario);
    case "missing_murl_links":
      return missingLinksResult(scenario);
    case "cross_origin_hint":
      return crossOriginResult(scenario);
    case "intermediary_rewrite":
      return intermediaryRewriteResult(scenario);
  }
}
